<script setup>
import { computed, ref, watch } from 'vue';
import Modal from './Modal.vue';
import Alert from '@/components/Alert.vue';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';
import {
  createEvalAgent,
  deleteEvalAgent,
  getEvalAgentById,
  updateEvalAgent,
} from '@/services/evalAgentService';
import { getEvalLLMs } from '@/services/evalLLMService';

const { SelectInput, TextInput } = useLayoutComponents();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['close', 'saved', 'delete']);
const { addToast } = useToast();

const isEditing = computed(() => !!props.agentId);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const showDeleteAlert = ref(false);
const isLoadingEvalLLMs = ref(false);
const evalLLMOptions = ref([]);
const harborEnvOptions = ref([
  { label: '(Use backend default)', value: '' },
  { label: 'modal', value: 'modal' },
  { label: 'daytona', value: 'daytona' },
]);
const harborModelPlaceholder = computed(() => (isLoadingEvalLLMs.value ? 'Loading models...' : 'Select a model'));

const formData = ref({
  name: '',
  harbor_env: 'modal',
  harbor_agent: '',
  harbor_model: '',
  harbor_n_concurrent: '',
  harbor_agent_setup_timeout_sec: '',
  harbor_subprocess_timeout_sec: '',
  active: true,
  agent_kwargs_json: '',
  harbor_args_json: '',
});

const jsonErrors = ref({
  agent_kwargs_json: '',
  harbor_args_json: '',
});

const resetForm = () => {
  formData.value = {
    name: '',
    harbor_env: 'modal',
    harbor_agent: '',
    harbor_model: '',
    harbor_n_concurrent: '',
    harbor_agent_setup_timeout_sec: '',
    harbor_subprocess_timeout_sec: '',
    active: true,
    agent_kwargs_json: '',
    harbor_args_json: '',
  };
  jsonErrors.value = {
    agent_kwargs_json: '',
    harbor_args_json: '',
  };
};

const toPrettyJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

const parseAgentFromResponse = (response) => {
  const data = response?.data;
  return data?.agent ?? data;
};

const loadEvalLLMs = async () => {
  isLoadingEvalLLMs.value = true;
  try {
    const response = await getEvalLLMs();
    const llms = Array.isArray(response?.data?.llms) ? response.data.llms : [];

    evalLLMOptions.value = llms
      .filter((llm) => Boolean(llm?.active))
      .map((llm) => {
        const hostname = llm?.hostname || '';
        const modelName = llm?.name || `LLM #${llm?.id}`;
        return {
          label: `openrouter/${hostname}/${modelName}`,
          value: String(llm.id),
        };
      })
      .filter((option) => option.value && option.label);
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to load Eval LLMs', 'error');
    evalLLMOptions.value = [];
  } finally {
    isLoadingEvalLLMs.value = false;
  }
};

const loadEvalAgent = async () => {
  if (!props.agentId) {
    resetForm();
    return;
  }

  isLoading.value = true;
  try {
    const response = await getEvalAgentById(props.agentId);
    const agent = parseAgentFromResponse(response);
    if (!agent) {
      throw new Error('Agent profile not found');
    }

    formData.value = {
      name: agent.name ?? '',
      harbor_env: agent.harbor_env ?? 'modal',
      harbor_agent: agent.harbor_agent ?? '',
      harbor_model: String(agent.harbor_model ?? ''),
      harbor_n_concurrent: agent.harbor_n_concurrent ?? '',
      harbor_agent_setup_timeout_sec: agent.harbor_agent_setup_timeout_sec ?? '',
      harbor_subprocess_timeout_sec: agent.harbor_subprocess_timeout_sec ?? '',
      active: Boolean(agent.active),
      agent_kwargs_json: agent.agent_kwargs ? toPrettyJson(agent.agent_kwargs) : '',
      harbor_args_json: Array.isArray(agent.harbor_args) ? toPrettyJson(agent.harbor_args) : '',
    };
    jsonErrors.value = { agent_kwargs_json: '', harbor_args_json: '' };
  } catch (error) {
    addToast(error.response?.data?.message || error.message || 'Failed to load agent profile', 'error');
  } finally {
    isLoading.value = false;
  }
};

const validateAgentKwargsJson = (value) => {
  if (!value || !String(value).trim()) {
    jsonErrors.value.agent_kwargs_json = '';
    return { valid: true, parsed: null };
  }
  try {
    const parsed = JSON.parse(value);
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
      throw new Error('agent_kwargs must be a JSON object');
    }
    jsonErrors.value.agent_kwargs_json = '';
    return { valid: true, parsed };
  } catch (error) {
    jsonErrors.value.agent_kwargs_json = error.message || 'Invalid JSON object';
    return { valid: false, parsed: null };
  }
};

const validateHarborArgsJson = (value) => {
  if (!value || !String(value).trim()) {
    jsonErrors.value.harbor_args_json = '';
    return { valid: true, parsed: null };
  }
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
      throw new Error('harbor_args must be a JSON array of strings');
    }
    jsonErrors.value.harbor_args_json = '';
    return { valid: true, parsed };
  } catch (error) {
    jsonErrors.value.harbor_args_json = error.message || 'Invalid JSON array';
    return { valid: false, parsed: null };
  }
};

const parseOptionalPositiveInt = (value, fieldLabel) => {
  if (value === '' || value === null || value === undefined) {
    return { valid: true, parsed: null };
  }
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return { valid: false, parsed: null, error: `${fieldLabel} must be a positive number` };
  }
  return { valid: true, parsed: Math.floor(num) };
};

const validateForm = () => {
  if (!String(formData.value.name || '').trim()) {
    addToast('Name is required', 'error');
    return false;
  }
  if (!String(formData.value.harbor_agent || '').trim()) {
    addToast('Harbor agent is required', 'error');
    return false;
  }
  if (!String(formData.value.harbor_model || '').trim()) {
    addToast('Harbor model is required', 'error');
    return false;
  }

  const kwargsValidation = validateAgentKwargsJson(formData.value.agent_kwargs_json);
  if (!kwargsValidation.valid) {
    addToast('Please fix the Agent Kwargs JSON before saving', 'error');
    return false;
  }
  const argsValidation = validateHarborArgsJson(formData.value.harbor_args_json);
  if (!argsValidation.valid) {
    addToast('Please fix the Harbor Args JSON before saving', 'error');
    return false;
  }

  const numericChecks = [
    parseOptionalPositiveInt(formData.value.harbor_n_concurrent, 'Concurrency'),
    parseOptionalPositiveInt(formData.value.harbor_agent_setup_timeout_sec, 'Agent setup timeout'),
    parseOptionalPositiveInt(formData.value.harbor_subprocess_timeout_sec, 'Subprocess timeout'),
  ];
  const invalidNumeric = numericChecks.find((item) => !item.valid);
  if (invalidNumeric) {
    addToast(invalidNumeric.error, 'error');
    return false;
  }

  return true;
};

const buildPayload = () => {
  const kwargsValidation = validateAgentKwargsJson(formData.value.agent_kwargs_json);
  const argsValidation = validateHarborArgsJson(formData.value.harbor_args_json);

  const harborEnv = String(formData.value.harbor_env || '').trim();
  const harborNConcurrent = parseOptionalPositiveInt(formData.value.harbor_n_concurrent, 'Concurrency').parsed;
  const harborAgentSetupTimeoutSec = parseOptionalPositiveInt(
    formData.value.harbor_agent_setup_timeout_sec,
    'Agent setup timeout',
  ).parsed;
  const harborSubprocessTimeoutSec = parseOptionalPositiveInt(
    formData.value.harbor_subprocess_timeout_sec,
    'Subprocess timeout',
  ).parsed;

  return {
    name: String(formData.value.name || '').trim(),
    active: Boolean(formData.value.active),
    harbor_env: harborEnv || null,
    harbor_agent: String(formData.value.harbor_agent || '').trim(),
    harbor_model: formData.value.harbor_model,
    harbor_n_concurrent: harborNConcurrent,
    harbor_agent_setup_timeout_sec: harborAgentSetupTimeoutSec,
    harbor_subprocess_timeout_sec: harborSubprocessTimeoutSec,
    agent_kwargs: kwargsValidation.parsed,
    harbor_args: argsValidation.parsed,
  };
};

const handleSave = async () => {
  if (!validateForm()) return;

  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (isEditing.value) {
      await updateEvalAgent(props.agentId, payload);
      addToast('Agent profile updated successfully', 'success');
    } else {
      await createEvalAgent(payload);
      addToast('Agent profile created successfully', 'success');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to save agent profile', 'error');
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async () => {
  if (!props.agentId) return;
  isDeleting.value = true;
  try {
    const response = await deleteEvalAgent(props.agentId);
    addToast(response.data?.message || 'Agent profile deleted successfully', 'success');
    emit('delete');
    emit('close');
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to delete agent profile', 'error');
  } finally {
    isDeleting.value = false;
    showDeleteAlert.value = false;
  }
};

const handleClose = () => {
  if (isSaving.value) return;
  resetForm();
  emit('close');
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      loadEvalLLMs();
      loadEvalAgent();
    }
  },
);

watch(
  () => props.agentId,
  () => {
    if (props.isOpen) {
      loadEvalAgent();
    }
  },
);
</script>

<template>
  <Modal
    :is-open="isOpen"
    :title="agentId ? 'Edit Eval Agent' : 'Create Eval Agent'"
    size="3xl"
    :close-on-click-outside="false"
    @close="handleClose"
  >
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <span class="sr-only">Loading...</span>
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
    </div>

    <form v-else @submit.prevent="handleSave" class="space-y-6 pb-2">
      <p class="text-sm text-white/60">
        {{ agentId ? 'Update this Harbor agent profile used for agentic evals.' : 'Create a Harbor agent profile for leaderboard/agentic runs.' }}
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="space-y-2 md:col-span-2">
          <label for="agent-name" class="block text-sm font-semibold text-white">
            Name <span class="text-red-500">*</span>
          </label>
          <TextInput
            id="agent-name"
            v-model="formData.name"
            type="text"
            required
            placeholder="Terminus GPT-5.2 (Modal)"
            :disabled="isSaving"
            bg-class="bg-echoes-background-black"
            border-class="border-echoes-border-light"
            hover-border-class="border-white/20"
            focus-border-class="border-echoes-primary"
            dataTestId="eval-agent-name-input"
          />
        </div>

        <div class="space-y-2">
          <label for="agent-harbor-env" class="block text-sm font-semibold text-white">Harbor Env</label>
          <SelectInput
            id="agent-harbor-env"
            v-model="formData.harbor_env"
            :disabled="isSaving"
            :options="harborEnvOptions"
            :placeholder="harborEnvOptions.find(option => option.value === formData.harbor_env)?.label || '(Use backend default)'"
            :hide-search="true"
            dataTestId="eval-agent-env-select"
            class="w-full"
          />
        </div>

        <div class="space-y-2">
          <label for="agent-status-toggle" class="block text-sm font-semibold text-white">Status</label>
          <div class="flex items-center gap-3 h-[42px]">
            <button
              id="agent-status-toggle"
              type="button"
              @click="formData.active = !formData.active"
              :disabled="isSaving"
              :class="[
                'relative inline-flex h-6 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed',
                formData.active ? 'bg-green-600' : 'bg-gray-600'
              ]"
              :aria-label="`Toggle agent status. Currently ${formData.active ? 'active' : 'inactive'}`"
              data-testid="eval-agent-active-toggle"
            >
              <span
                aria-hidden="true"
                :class="[
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                  formData.active ? 'translate-x-6' : 'translate-x-0'
                ]"
              />
            </button>
            <span :class="formData.active ? 'text-green-400 text-sm font-medium' : 'text-gray-400 text-sm font-medium'">
              {{ formData.active ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>

        <div class="space-y-2">
          <label for="agent-harbor-agent" class="block text-sm font-semibold text-white">
            Harbor Agent <span class="text-red-500">*</span>
          </label>
          <TextInput
            id="agent-harbor-agent"
            v-model="formData.harbor_agent"
            type="text"
            required
            placeholder="terminus-2"
            :disabled="isSaving"
            bg-class="bg-echoes-background-black"
            border-class="border-echoes-border-light"
            hover-border-class="border-white/20"
            focus-border-class="border-echoes-primary"
            dataTestId="eval-agent-agent-input"
          />
        </div>

        <div class="space-y-2">
          <label for="agent-harbor-model" class="block text-sm font-semibold text-white">
            Harbor Model <span class="text-red-500">*</span>
          </label>
          <SelectInput
            id="agent-harbor-model"
            v-model="formData.harbor_model"
            :disabled="isSaving || isLoadingEvalLLMs"
            :options="evalLLMOptions"
            :placeholder="formData.harbor_model ? formData.harbor_model : (isLoadingEvalLLMs ? 'Loading models...' : 'Select a model')"
            dataTestId="eval-agent-model-input"
            class="w-full"
          />
        </div>

        <div class="space-y-2">
          <label for="agent-n-concurrent" class="block text-sm font-semibold text-white">Concurrency</label>
          <TextInput
            id="agent-n-concurrent"
            v-model="formData.harbor_n_concurrent"
            type="number"
            min="1"
            step="1"
            placeholder="1"
            :disabled="isSaving"
            bg-class="bg-echoes-background-black"
            border-class="border-echoes-border-light"
            hover-border-class="border-white/20"
            focus-border-class="border-echoes-primary"
            dataTestId="eval-agent-n-concurrent-input"
          />
        </div>

        <div class="space-y-2">
          <label for="agent-setup-timeout" class="block text-sm font-semibold text-white">Agent Setup Timeout (sec)</label>
          <TextInput
            id="agent-setup-timeout"
            v-model="formData.harbor_agent_setup_timeout_sec"
            type="number"
            min="1"
            step="1"
            placeholder="900"
            :disabled="isSaving"
            bg-class="bg-echoes-background-black"
            border-class="border-echoes-border-light"
            hover-border-class="border-white/20"
            focus-border-class="border-echoes-primary"
            dataTestId="eval-agent-setup-timeout-input"
          />
        </div>

        <div class="space-y-2 md:col-span-2">
          <label for="agent-subprocess-timeout" class="block text-sm font-semibold text-white">Subprocess Timeout (sec)</label>
          <TextInput
            id="agent-subprocess-timeout"
            v-model="formData.harbor_subprocess_timeout_sec"
            type="number"
            min="1"
            step="1"
            placeholder="7200"
            :disabled="isSaving"
            bg-class="bg-echoes-background-black"
            border-class="border-echoes-border-light"
            hover-border-class="border-white/20"
            focus-border-class="border-echoes-primary"
            dataTestId="eval-agent-subprocess-timeout-input"
          />
        </div>
      </div>

      <div class="space-y-2">
        <label for="agent-kwargs" class="block text-sm font-semibold text-white">Agent Kwargs (JSON object)</label>
        <TextInput
          id="agent-kwargs"
          :model-value="formData.agent_kwargs_json"
          type="text"
          :is-textarea="true"
          :rows="6"
          placeholder='{"model_info":{"max_tokens":128000}}'
          :disabled="isSaving"
          bg-class="bg-echoes-background-black"
          border-class="border-echoes-border-light"
          hover-border-class="border-white/20"
          focus-border-class="border-echoes-primary"
          class="font-mono text-sm"
          dataTestId="eval-agent-kwargs-input"
          @update:model-value="(value) => { formData.agent_kwargs_json = value; validateAgentKwargsJson(value); }"
        />
        <p v-if="jsonErrors.agent_kwargs_json" class="text-xs text-red-400">{{ jsonErrors.agent_kwargs_json }}</p>
        <p class="text-xs text-white/50">Optional. Must be a JSON object.</p>
      </div>

      <div class="space-y-2">
        <label for="agent-harbor-args" class="block text-sm font-semibold text-white">Harbor Args (JSON array)</label>
        <TextInput
          id="agent-harbor-args"
          :model-value="formData.harbor_args_json"
          type="text"
          :is-textarea="true"
          :rows="4"
          placeholder='["--some-flag","value"]'
          :disabled="isSaving"
          bg-class="bg-echoes-background-black"
          border-class="border-echoes-border-light"
          hover-border-class="border-white/20"
          focus-border-class="border-echoes-primary"
          class="font-mono text-sm"
          dataTestId="eval-agent-harbor-args-input"
          @update:model-value="(value) => { formData.harbor_args_json = value; validateHarborArgsJson(value); }"
        />
        <p v-if="jsonErrors.harbor_args_json" class="text-xs text-red-400">{{ jsonErrors.harbor_args_json }}</p>
        <p class="text-xs text-white/50">Optional. Must be a JSON array of strings.</p>
      </div>

      <div class="border-t border-echoes-border-light" />

      <div class="flex items-center justify-between">
        <button
          v-if="isEditing"
          type="button"
          @click="showDeleteAlert = true"
          :disabled="isDeleting || isSaving"
          class="inline-flex items-center justify-center px-4 py-1.5 text-sm font-medium rounded-lg bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="eval-agent-delete-button"
        >
          <i class="mdi mdi-delete mr-2 text-red-500"></i>
          Delete
        </button>
        <div v-else />

        <div class="flex items-center gap-3">
          <button
            type="button"
            @click="handleClose"
            :disabled="isSaving"
            class="inline-flex items-center justify-center px-4 py-1.5 text-sm font-medium rounded-lg bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="eval-agent-cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="inline-flex items-center justify-center px-4 py-1.5 text-sm font-medium rounded-lg bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="eval-agent-save-button"
          >
            <i v-if="isSaving" class="mdi mdi-loading animate-spin mr-2" />
            {{ isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Agent') }}
          </button>
        </div>
      </div>
    </form>
  </Modal>

  <Alert
    :show="showDeleteAlert"
    title="Delete agent profile?"
    message="This Harbor agent profile will be deleted permanently."
    type="warning"
    confirmText="Delete"
    cancelText="Cancel"
    @confirm="handleDelete"
    @close="showDeleteAlert = false"
  />
</template>
